import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft, FolderTree, Lock, Plus } from 'lucide-react';
import type { StockGroup } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { StockGroupFormDialog } from '../components/inventory/stock-group-form-dialog.js';
import { StockGroupImage } from '../components/inventory/stock-group-image.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, useConfirm, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useDeleteStockGroup, useStockGroups } from '../lib/inventory/use-stock.js';

/**
 * The headings a shop reads its shelf under.
 *
 * A shop does not think of stock as a flat list — it thinks "t shirt, by the metre" and "buttons,
 * by the piece", and reads the items beneath that. So each row leads with the picture and the
 * name, says what the heading is bought in, and its main action is to OPEN THE SHELF under it:
 * this screen exists to get somebody to the items, not to be somewhere they stay.
 *
 * Deleting is the part that needed saying out loud. It removes the heading and nothing else —
 * the items keep their colours, their quantities and their whole movement history, and simply
 * come loose. The confirmation says that in those words, because "delete group" reads to most
 * people like "delete the twelve fabrics inside it", and a shop that believes that will never
 * touch the button.
 */
export function StockGroupsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StockGroup | null>(null);

  // The same refusal `StockPage` makes, for the same reason: every line under these headings
  // carries a cost price.
  const allowed = role !== 'tailor';
  const groups = useStockGroups({ includeInactive }, { enabled: allowed });
  const remove = useDeleteStockGroup();

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('stockGroups.title')} />
        <EmptyState
          icon={Lock}
          title={t('stock.noAccessTitle')}
          description={t('stock.noAccessHint')}
        />
      </>
    );
  }

  const onDelete = async (group: StockGroup): Promise<void> => {
    const confirmed = await confirm({
      title: t('stockGroups.deleteTitle', { name: group.name }),
      // Says plainly what survives: the items are kept, and become ungrouped.
      description: t('stockGroups.deleteDescription', { count: group.itemCount }),
      confirmLabel: t('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;

    try {
      const result = await remove.mutateAsync(group.id);
      toast({
        title: t('stockGroups.deleted', {
          name: group.name,
          count: result.ungroupedItemCount,
        }),
        variant: 'success',
      });
    } catch {
      toast({ title: t('error.unexpected'), variant: 'destructive' });
    }
  };

  const items = groups.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('stockGroups.title')}
        description={t('stockGroups.description')}
        action={
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            {t('stockGroups.add')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <Link
          to="/stock"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('stock.backToList')}
        </Link>

        <label className="flex items-center gap-2 self-start text-sm">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          {t('stockGroups.showRetired')}
        </label>

        {groups.isPending ? <ListSkeleton rows={4} /> : null}

        {groups.isError ? <LoadError onRetry={() => void groups.refetch()} /> : null}

        {groups.data && items.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title={t('stockGroups.emptyTitle')}
            description={t('stockGroups.emptyDescription')}
            action={<Button onClick={() => setAdding(true)}>{t('stockGroups.add')}</Button>}
          />
        ) : null}

        {items.length > 0 ? (
          <ul className="flex flex-col gap-1.5">
            {items.map((group) => (
              <li
                key={group.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3 text-sm"
              >
                <StockGroupImage mediaId={group.imageMediaId} alt={group.name} />

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{group.name}</span>
                    {group.active ? null : <Badge>{t('stockGroups.retired')}</Badge>}
                  </span>
                  <span className="block text-muted-foreground">
                    {t(`stock.unit.${group.unit}`)} ·{' '}
                    {t('stockGroups.itemCount', { count: group.itemCount })}
                  </span>
                </span>

                <span className="flex flex-wrap items-center gap-1.5">
                  {/* The point of the screen: get to the shelf under this heading. */}
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/stock?groupId=${group.id}`}>{t('stockGroups.openItems')}</Link>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(group)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    disabled={remove.isPending}
                    onClick={() => void onDelete(group)}
                  >
                    {t('common.delete')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <StockGroupFormDialog open={adding} onOpenChange={setAdding} />

      {editing ? (
        <StockGroupFormDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          group={editing}
          onSaved={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

export default StockGroupsPage;
